import { useMemo } from "react";
import { Row } from "@hightouch/htui";

import { useContentEmailComponentsQuery } from "src/graphql";
import { ComponentThumbnail } from "./component-thumbnail";
import type { SelectedComponent } from "./types";

type PersistedComponent = {
  id: string;
  name: string;
  componentType: string | null;
};

type Props = {
  components: PersistedComponent[];
};

/**
 * Renders the components attached to a sent message as read-only thumbnails
 * (small square + hover preview). History persists only the reference
 * (id/name/type), so the current HTML is re-fetched by id here for the preview;
 * a component deleted since send simply renders without a preview.
 */
export const MessageComponentThumbnails = ({ components }: Props) => {
  const ids = useMemo(() => components.map((c) => c.id), [components]);

  const { data } = useContentEmailComponentsQuery(
    { filters: { id: { _in: ids } } },
    { enabled: ids.length > 0 },
  );

  const htmlById = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const row of data?.content_emails ?? []) {
      map.set(row.id, row.content_email_version?.resolved_html?.html ?? null);
    }
    return map;
  }, [data]);

  return (
    <Row gap={2} overflowX="auto">
      {components.map((component) => {
        const resolved: SelectedComponent = {
          id: component.id,
          name: component.name,
          componentType: component.componentType,
          html: htmlById.get(component.id) ?? null,
        };
        return (
          <ComponentThumbnail
            key={component.id}
            component={resolved}
            // History is read-only; the thumbnail's remove affordance is a no-op.
            onRemove={() => {}}
            readOnly
          />
        );
      })}
    </Row>
  );
};
